import type { AdminProviderRegistryStore } from "@equipped/core/admin/adminProviderRegistryStore";
import {
  AdminProviderConnection,
  AdminProviderRegistry,
  normalizeAdminBaseUrl,
} from "@equipped/core/admin/adminProviderRegistry";

const REGISTRY_KEY = "equipped-admin-web-mantis:admin-provider-registry";

function browserLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function parseConnection(value: unknown): AdminProviderConnection | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<AdminProviderConnection>;
  const baseUrl = typeof candidate.baseUrl === "string" ? normalizeAdminBaseUrl(candidate.baseUrl) : null;
  const auth = candidate.auth;
  if (
    !baseUrl ||
    typeof candidate.id !== "string" ||
    !candidate.id ||
    typeof candidate.label !== "string" ||
    !auth ||
    (auth.kind !== "session" && auth.kind !== "adminKey") ||
    typeof auth.hasCredential !== "boolean" ||
    typeof candidate.addedAt !== "number" ||
    !Number.isFinite(candidate.addedAt)
  ) {
    return null;
  }

  return {
    id: candidate.id,
    baseUrl,
    label: candidate.label,
    auth: { kind: auth.kind, hasCredential: auth.hasCredential },
    ...(typeof candidate.contractVersion === "string"
      ? { contractVersion: candidate.contractVersion }
      : {}),
    addedAt: candidate.addedAt,
  };
}

function parseRegistry(value: unknown): AdminProviderRegistry {
  if (!value || typeof value !== "object") return { connections: [], activeId: null };
  const candidate = value as { connections?: unknown; activeId?: unknown };
  if (!Array.isArray(candidate.connections)) return { connections: [], activeId: null };

  const ids = new Set<string>();
  const baseUrls = new Set<string>();
  const connections: AdminProviderConnection[] = [];
  for (const value of candidate.connections) {
    const connection = parseConnection(value);
    if (!connection || ids.has(connection.id) || baseUrls.has(connection.baseUrl)) continue;
    ids.add(connection.id);
    baseUrls.add(connection.baseUrl);
    connections.push(connection);
  }
  const activeId = typeof candidate.activeId === "string" && ids.has(candidate.activeId)
    ? candidate.activeId
    : (connections[0]?.id ?? null);
  return { connections, activeId };
}

/** Browser persistence for non-secret provider metadata. Credentials use authStorage.ts. */
export class LocalAdminProviderRegistryStore implements AdminProviderRegistryStore {
  constructor(private readonly storage: Storage | null = browserLocalStorage()) {}

  /** Synchronous bootstrap read keeps auth from briefly mounting for the wrong provider. */
  loadSnapshot(): AdminProviderRegistry {
    try {
      const raw = this.storage?.getItem(REGISTRY_KEY);
      return raw ? parseRegistry(JSON.parse(raw) as unknown) : { connections: [], activeId: null };
    } catch {
      return { connections: [], activeId: null };
    }
  }

  async load(): Promise<AdminProviderRegistry> {
    return this.loadSnapshot();
  }

  async save(registry: AdminProviderRegistry): Promise<void> {
    try {
      // AdminProviderConnection contains only metadata and a credential-presence bit.
      // Session bearers and admin keys deliberately live in separate provider slots.
      this.storage?.setItem(REGISTRY_KEY, JSON.stringify(parseRegistry(registry)));
    } catch {
      // Storage denial/full quota is non-fatal, matching the existing auth store.
    }
  }
}
